import { useCallback, useEffect, useRef, useState } from 'react';
import { api, callV1 } from '../api';
import {
  demoGroupName, demoTemplate, req, INITIAL_VARIABLES,
} from './wizardActions';

// Secrets for the demo screens live here for the tab's lifetime only: they are
// returned exactly once at creation, and a reload needs them again to rebuild
// the iframe URLs. sessionStorage (not localStorage) so closing the tab forgets
// them, and so two tabs can't fight over one set of screens.
const SESSION_KEY = 'puttv_wizard_session';

function loadSession() {
  try { return JSON.parse(sessionStorage.getItem(SESSION_KEY)) || null; } catch (_) { return null; }
}
function saveSession(s) {
  try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(s)); } catch (_) { /* private mode */ }
}
function clearSession() {
  try { sessionStorage.removeItem(SESSION_KEY); } catch (_) { /* ignore */ }
}

/**
 * Everything the wizard needs before a user can do anything: two live screens,
 * an API key, a saved template, a data source, and both screens in one group.
 *
 * This is the step 0 that deliberately isn't a step. Provisioning a screen and
 * minting a key cost a user ~35 seconds and teach nothing, so they happen here
 * while the page is loading and the QR/pairing animation plays. By the time the
 * first button is clickable, one call already reaches two real screens.
 */
export default function useDemoSession({ userId, userEmail, enabled = true }) {
  const [state, setState] = useState({
    phase: 'idle',        // idle | provisioning | ready | error | disabled
    screens: [],          // [{ deviceUUID, deviceSecret, name }]
    apiKey: null,
    apiKeyId: null,
    templateId: null,
    templateJson: null,   // kept so the wizard can SHOW the template, not just push its id
    sourceId: null,
    group: null,
    error: null,
    progress: '',
  });
  // Guards React 18 StrictMode's double-invoked effects from provisioning twice.
  const startedRef = useRef(false);

  const log = useRef([]);
  const [events, setEvents] = useState([]);
  const pushEvent = useCallback((entry) => {
    log.current = [...log.current.slice(-199), { ...entry, at: Date.now() }];
    setEvents(log.current);
  }, []);

  // Every /api/v1 call the wizard makes funnels through here so each one lands
  // in the event log — including the setup calls, which is how the log explains
  // what the page did before the user arrived.
  const run = useCallback(async (request, key) => {
    const result = await callV1(request.path, {
      method: request.method,
      body: request.body,
      key: request.keyOverride || key,
    });
    pushEvent({
      kind: result.ok ? 'ok' : 'fail',
      label: request.label,
      method: request.method,
      path: request.path,
      status: result.status,
      ms: result.ms,
      body: result.body,
    });
    return result;
  }, [pushEvent]);

  const provision = useCallback(async ({ fresh = false } = {}) => {
    setState((s) => ({ ...s, phase: 'provisioning', error: null, progress: 'Creating screens…' }));

    try {
      let session = fresh ? null : loadSession();
      if (fresh) {
        try { await api.deleteDemoScreens(userId); } catch (_) { /* nothing to remove */ }
        clearSession();
      }

      // --- screens ---
      const res = await api.createDemoScreens(2, userId, userEmail);
      let screens = res.screens.map((s) => ({
        deviceUUID: s.deviceUUID,
        name: s.name,
        // A reused screen comes back without a secret (shown once at creation),
        // so fall back to the one this tab already stashed.
        deviceSecret: s.deviceSecret
          || (session && (session.secrets || {})[s.deviceUUID])
          || null,
      }));

      // A reused screen whose secret this tab never saw can't be rendered — the
      // iframe has no way to authenticate. Start over with fresh ones rather
      // than show a broken frame.
      if (screens.some((s) => !s.deviceSecret)) {
        if (fresh) throw new Error('Could not obtain screen credentials');
        return provision({ fresh: true });
      }

      setState((s) => ({ ...s, screens, progress: 'Minting an API key…' }));

      // --- api key ---
      // Reused within the tab: the plaintext is unrecoverable, so minting on
      // every reload would litter Settings with dead keys.
      let apiKey = session && session.apiKey;
      let apiKeyId = session && session.apiKeyId;
      if (!apiKey) {
        // POST /api/keys answers { key, ...row }; `key` is plaintext exactly once.
        const created = await api.createApiKey('Wizard (demo)', userId, userEmail);
        apiKey = created.key;
        apiKeyId = created.id || null;
      }
      if (!apiKey) throw new Error('Could not create an API key for the demo');

      // --- is the external API even switched on? ---
      const probe = await run(req.listDevices(), apiKey);
      if (probe.status === 503) {
        setState((s) => ({
          ...s, phase: 'disabled', screens, apiKey, apiKeyId,
          error: 'The external API is switched off on this server. Set API_KEYS in the '
               + 'server environment to enable /api/v1 — the wizard drives the same public '
               + 'API an integrator would, so it cannot run without it.',
        }));
        return;
      }
      if (probe.status === 401) throw new Error('The demo API key was rejected');

      setState((s) => ({ ...s, progress: 'Preparing content…' }));

      // --- data source, template, group ---
      const sourceRes = await run(req.createDataSource(), apiKey);
      const sourceId = sourceRes.body && sourceRes.body.data_source && sourceRes.body.data_source.id;

      const templateJson = demoTemplate(sourceId);
      const tplRes = await run(req.createTemplate(templateJson), apiKey);
      const templateId = tplRes.body && tplRes.body.template && tplRes.body.template.id;
      if (!templateId) throw new Error('Could not save the demo template');

      const group = demoGroupName(userId);
      for (const [i, sc] of screens.entries()) {
        await run(req.joinGroup(sc.deviceUUID, group, `Demo screen ${i + 1}`), apiKey);
        // Seed variables so the very first push renders words, not {{placeholders}}.
        await run(req.setVariables(sc.deviceUUID, INITIAL_VARIABLES, true), apiKey);
      }

      saveSession({
        apiKey, apiKeyId,
        secrets: Object.fromEntries(screens.map((s) => [s.deviceUUID, s.deviceSecret])),
      });

      setState({
        phase: 'ready', screens, apiKey, apiKeyId, templateId, templateJson, sourceId, group,
        error: null, progress: '',
      });
    } catch (e) {
      setState((s) => ({ ...s, phase: 'error', error: e.message || 'Setup failed' }));
    }
  }, [userId, userEmail, run]);

  useEffect(() => {
    if (!enabled || startedRef.current) return;
    startedRef.current = true;
    provision();
  }, [enabled, provision]);

  // Tear the session down: screens, key and stashed secrets. Used by "start
  // over" and by the exit ramp so an evaluation leaves nothing behind.
  const teardown = useCallback(async () => {
    const session = loadSession();
    try { await api.deleteDemoScreens(userId); } catch (_) { /* already gone */ }
    if (session && session.apiKeyId) {
      try { await api.revokeApiKey(session.apiKeyId, userId); } catch (_) { /* already revoked */ }
    }
    clearSession();
  }, [userId]);

  const restart = useCallback(async () => {
    startedRef.current = true;
    log.current = [];
    setEvents([]);
    await teardown();
    await provision({ fresh: true });
  }, [teardown, provision]);

  return { ...state, events, run, pushEvent, restart, teardown };
}
